/**
 * Cookie-consent composable — the `emalino_cookie_consent` js-cookie (365d).
 *
 * ── Two-store boundary contract (read before editing) ───────────────────────
 * Consent lives in TWO deliberately-decoupled stores:
 *
 *   1. This cookie (`emalino_cookie_consent`) — the SOLE authority for
 *      "has the visitor answered the banner?" → drives whether
 *      `CookieConsentBanner` shows.
 *   2. `store-user-settings.consent.*` (Pinia, see `stores/userSettings.ts`) —
 *      the authority for the legal matrix (cookie/terms/privacy granted) →
 *      drives the auth gate (`hasCookieConsent` / `hasEssentialConsents`).
 *
 * They routinely diverge for legitimate reasons (anonymous visitor never grants
 * terms/privacy; Reject All zeroes the store flag; logout resets the store but
 * not this cookie). **Neither store may delete or overwrite the other based on
 * the other's state.** A cross-store wipe here (deleting the cookie when the two
 * disagreed) was the root cause of the "consent lost on reload" bug (`20689f0f`).
 * Boot-time re-sync (cookie → store) is allowed and lives in ONE place:
 * `userSettingsStore.reconcileCookieConsentFromBanner()` — never wipe.
 *
 * See `memory/frontend-cookie-consent-two-store.md` and
 * `wiki/future/todo/20260530_071756_consent_two_store_decoupling_hardening.md`.
 */

import { ref, computed } from 'vue';
import Cookies from 'js-cookie';
import { devWarn, platform } from '@shared/vue/utils/debug';

// Module-level shared state — every consumer of useCookieConsent sees the same
// instance. Used to let any component (e.g. the public Footer's "Cookie
// preferences" link) ask the CookieConsentBanner to re-open itself, satisfying
// the GDPR requirement that users can withdraw / change consent at any time.
const reopenRequested = ref(0);

// Tracks whether CookieConsentBanner is currently visible. Set by the banner
// component itself via setBannerVisible(). Lets ConsentRequiredNotice suppress
// its "Review and Accept" action button while the popup is already open.
const bannerVisible = ref(false);

/**
 * Cookie consent preferences
 */
export interface CookieConsentPreferences {
  version: string;
  categories: Record<string, boolean>;
  consentedAt: number;
  expiresAt: number;
}

const COOKIE_NAME = 'emalino_cookie_consent';
const COOKIE_EXPIRES_DAYS = 365;

/**
 * Shape-validate a parsed cookie value. The cookie is user-editable; a
 * malformed value (e.g. missing expiresAt) must read as no-consent, not as
 * consent: `undefined < Date.now()` is false, so an unchecked cast would let
 * hasConsent() return true on a GDPR surface.
 */
function isCookieConsentPreferences(value: unknown): value is CookieConsentPreferences {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.version === 'string'
    && typeof candidate.categories === 'object'
    && candidate.categories !== null
    && typeof candidate.consentedAt === 'number'
    && typeof candidate.expiresAt === 'number'
  );
}

/**
 * Cookie consent composable
 */
export function useCookieConsent() {
  const preferences = ref<CookieConsentPreferences | null>(null);

  /**
   * Load preferences from cookie
   */
  function loadPreferences(): void {
    try {
      const cookieValue = Cookies.get(COOKIE_NAME);
      if (!cookieValue) {
        preferences.value = null;
        return;
      }

      const parsed: unknown = JSON.parse(cookieValue);
      if (!isCookieConsentPreferences(parsed)) {
        devWarn('[CookieConsent] Malformed consent cookie; treating as no consent');
        preferences.value = null;
        return;
      }
      preferences.value = parsed;
    } catch (err) {
      devWarn('[CookieConsent] Failed to parse preferences:', err);
      preferences.value = null;
    }
  }

  /**
   * Save consent preferences
   */
  function saveConsent(
    version: string,
    categories: Record<string, boolean>
  ): void {
    const now = Date.now();
    const expiresAt = now + COOKIE_EXPIRES_DAYS * 24 * 60 * 60 * 1000;

    const newPreferences: CookieConsentPreferences = {
      version,
      categories,
      consentedAt: now,
      expiresAt,
    };

    // Save to cookie. `secure: true` is silently rejected by WebKit when the
    // page wasn't loaded over HTTPS — Capacitor iOS uses the custom scheme
    // `App://localhost`, which doesn't qualify, so the cookie would never
    // persist and the banner would re-prompt on every navigation. Only flag
    // cookies as Secure on the web in production.
    Cookies.set(COOKIE_NAME, JSON.stringify(newPreferences), {
      expires: COOKIE_EXPIRES_DAYS,
      path: '/',
      sameSite: 'Lax',
      secure: import.meta.env.PROD && !platform.isNativeApp(),
    });

    preferences.value = newPreferences;
  }

  /**
   * Check if user has consented
   */
  function hasConsent(): boolean {
    loadPreferences();

    if (!preferences.value) {
      return false;
    }

    // Check if consent expired
    if (preferences.value.expiresAt < Date.now()) {
      return false;
    }

    return true;
  }

  /**
   * Check if consent needs refresh due to version change
   */
  function needsReconsent(currentVersion: string): boolean {
    if (!preferences.value) {
      return true;
    }

    return preferences.value.version !== currentVersion;
  }

  /**
   * Check if specific category is consented
   */
  function hasCategory(category: string): boolean {
    if (!preferences.value) {
      return false;
    }

    return preferences.value.categories[category] === true;
  }

  /**
   * Clear consent (for testing/debugging)
   */
  function clearConsent(): void {
    Cookies.remove(COOKIE_NAME, { path: '/' });
    preferences.value = null;
  }

  /**
   * Ask the CookieConsentBanner to re-open. The banner watches the shared
   * `reopenRequested` counter and shows itself when it changes. Use this from
   * a "Cookie preferences" link in the footer or settings page so users can
   * change consent at any time (GDPR right to withdraw consent).
   */
  function requestReopen(): void {
    reopenRequested.value++;
  }

  // Initialize
  loadPreferences();

  return {
    preferences: computed(() => preferences.value),
    hasConsent,
    needsReconsent,
    hasCategory,
    saveConsent,
    clearConsent,
    requestReopen,
    reopenRequested: computed(() => reopenRequested.value),
    bannerVisible: computed(() => bannerVisible.value),
    setBannerVisible: (visible: boolean) => {
      bannerVisible.value = visible;
    },
  };
}
